import type { CatalogEntry } from '../types';

/**
 * Admin (feature `90`) error codes + catalog. `apps/admin` surfaces live in a
 * high band so they never collide with product features as those grow.
 *
 * Code shape is `90AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * `Shared` codes (`9000xx`, AA=00) are recurring cross-endpoint conditions.
 *
 * Endpoint → `AA` map:
 *   login=01 (POST /admin/auth/login),
 *   02 = RETIRED, do not reuse. It belonged to the removed credential re-key
 *        endpoint + worker (POST /admin/credential-rekey); `9002xx` codes may
 *        still appear in old logs.
 *   03 = RETIRED, do not reuse. It belonged to the removed manual subscription
 *        assignment (POST /admin/organizations/:id/subscription); `9003xx`
 *        codes may still appear in old logs.
 *   04 = RETIRED, do not reuse. It belonged to a removed admin ClickHouse
 *        warmupEmails backfill endpoint; `9004xx` codes may still appear in
 *        old logs, so a new endpoint must take `05`.
 */
export const ADMIN_CODES = {
  /** Cross-endpoint conditions (AA=00). */
  Shared: {
    OrganizationNotFound: '900001',
    EmailAccountNotFound: '900002',
  },
  /** login=01 (POST /admin/auth/login). */
  Login: {
    InvalidCredentials: '900101',
  },
} as const;

export const ADMIN_CATALOG: Record<string, CatalogEntry> = {
  // ── Shared (cross-endpoint) ──
  '900001': { httpStatus: 404, message: "We couldn't find that organization." },
  '900002': { httpStatus: 404, message: "We couldn't find that email account." },

  // ── Login ──
  '900101': { httpStatus: 401, message: 'Invalid credentials.' },
};
